import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Card, CardContent, Grid, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Button, Chip,
    TextField, MenuItem, Tabs, Tab, InputAdornment, Avatar, Dialog,
    DialogTitle, DialogContent, DialogActions, IconButton, LinearProgress,
    CircularProgress, Autocomplete, Tooltip
} from '@mui/material';
import {
    Search as SearchIcon, Download as DownloadIcon, Add as AddIcon,
    CurrencyRupee as RupeeIcon, School as SchoolIcon,
    CheckCircle as CheckIcon, Schedule as PendingIcon,
    AccountBalance as AccountIcon, Edit as EditIcon, Delete as DeleteIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import axios from 'axios';
import { studentAPI } from '../services/api';

const Scholarship = () => {
    const [tab, setTab] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [classFilter, setClassFilter] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    
    const [scholarships, setScholarships] = useState([]);
    const [students, setStudents] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [formData, setFormData] = useState({ 
        studentId: '', 
        schemeName: '', 
        amount: '', 
        disbursedAmount: 0, 
        status: 'Applied', 
        bankName: '', 
        accountNo: '', 
        disbursementDate: '',
        academicYearId: 1 // Default or from context
    });

    const fetchScholarships = async () => {
        setLoading(true);
        try {
            const res = await axios.get('/api/v1/scholarships');
            setScholarships(res.data.data || []);
        } catch (error) {
            toast.error('Failed to load scholarships');
        } finally {
            setLoading(false);
        }
    };

    const fetchStudents = async () => {
        try {
            const res = await studentAPI.getAll();
            setStudents(res.data?.data || []);
        } catch (error) {
            console.error('Failed to load students');
        }
    };

    useEffect(() => {
        fetchScholarships();
        fetchStudents();
    }, []);

    const handleOpenAssign = (item = null) => {
        if (item) {
            setSelectedId(item.id);
            setFormData({
                studentId: item.studentId,
                schemeName: item.schemeName,
                amount: item.amount,
                disbursedAmount: item.disbursedAmount,
                status: item.status,
                bankName: item.bankName || '',
                accountNo: item.accountNo || '',
                disbursementDate: item.disbursementDate ? item.disbursementDate.split('T')[0] : '',
                academicYearId: item.academicYearId
            });
        } else {
            setSelectedId(null);
            setFormData({ 
                studentId: '', schemeName: '', amount: '', disbursedAmount: 0, 
                status: 'Applied', bankName: '', accountNo: '', disbursementDate: '', academicYearId: 1 
            });
        }
        setOpenDialog(true);
    };

    const handleSubmit = async () => {
        if (!formData.studentId || !formData.schemeName || !formData.amount) {
            toast.warning('Please fill required fields');
            return;
        }
        setSubmitting(true);
        try {
            if (selectedId) {
                await axios.put(`/api/v1/scholarships/${selectedId}`, formData);
                toast.success('Scholarship updated');
            } else {
                await axios.post('/api/v1/scholarships', formData);
                toast.success('Scholarship assigned');
            }
            setOpenDialog(false);
            fetchScholarships();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Action failed');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this record?')) return;
        try {
            await axios.delete(`/api/v1/scholarships/${id}`);
            toast.success('Deleted successfully');
            fetchScholarships();
        } catch (error) {
            toast.error('Failed to delete');
        }
    };

    const filteredData = scholarships.filter(s => {
        const student = s.Student || {};
        const fullName = `${student.user?.firstName || ''} ${student.user?.lastName || ''}`.toLowerCase();
        
        const matchTab = tab === 0 ? true : 
                         tab === 1 ? s.status === 'Disbursed' : 
                         tab === 2 ? (s.status === 'Pending' || s.status === 'Applied') : 
                         s.status === 'Partial';
        
        const matchClass = !classFilter || student.Class?.name === classFilter;
        const matchCategory = !categoryFilter || student.category === categoryFilter;
        const matchSearch = !searchTerm || 
                            fullName.includes(searchTerm.toLowerCase()) || 
                            s.schemeName.toLowerCase().includes(searchTerm.toLowerCase());
        
        return matchTab && matchClass && matchCategory && matchSearch;
    });

    const totalAmount = scholarships.reduce((a, s) => a + Number(s.amount), 0);
    const totalDisbursed = scholarships.reduce((a, s) => a + Number(s.disbursedAmount), 0);
    const disbursedCount = scholarships.filter(s => s.status === 'Disbursed').length;
    const pendingCount = scholarships.filter(s => s.status === 'Pending' || s.status === 'Applied').length;

    const uniqueClasses = [...new Set(scholarships.map(s => s.Student?.Class?.name).filter(Boolean))];

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" fontWeight={700}>Scholarship Management</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="contained" startIcon={<AddIcon />} size="small" onClick={() => handleOpenAssign()}>Assign Scholarship</Button>
                    <Button variant="outlined" startIcon={<DownloadIcon />} size="small">Export</Button>
                </Box>
            </Box>

            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                    <Card sx={{ borderLeft: '4px solid #1a9e8f' }}>
                        <CardContent sx={{ py: 1.5 }}>
                            <Typography variant="body2" color="text.secondary">Total Scholarships</Typography>
                            <Typography variant="h5" fontWeight={700}>{scholarships.length}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card sx={{ borderLeft: '4px solid #2196f3' }}>
                        <CardContent sx={{ py: 1.5 }}>
                            <Typography variant="body2" color="text.secondary">Total Amount</Typography>
                            <Typography variant="h5" fontWeight={700}>₹{(totalAmount / 1000).toFixed(1)}K</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card sx={{ borderLeft: '4px solid #4caf50' }}>
                        <CardContent sx={{ py: 1.5 }}>
                            <Typography variant="body2" color="text.secondary">Disbursed</Typography>
                            <Typography variant="h5" fontWeight={700} color="success.main">₹{(totalDisbursed / 1000).toFixed(1)}K</Typography>
                            <LinearProgress variant="determinate" value={totalAmount > 0 ? (totalDisbursed / totalAmount) * 100 : 0}
                                sx={{ mt: 0.5, height: 4, borderRadius: 2 }} />
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card sx={{ borderLeft: '4px solid #ff9800' }}>
                        <CardContent sx={{ py: 1.5 }}>
                            <Typography variant="body2" color="text.secondary">Pending</Typography>
                            <Typography variant="h5" fontWeight={700} color="warning.main">{pendingCount}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Card>
                <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
                    <Tab label={`All (${scholarships.length})`} />
                    <Tab label={`Disbursed (${disbursedCount})`} />
                    <Tab label={`Pending (${pendingCount})`} />
                    <Tab label="Partial" />
                </Tabs>
                <Box sx={{ p: 2 }}>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={5}>
                            <TextField fullWidth size="small" placeholder="Search by name or scheme..." value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField fullWidth select size="small" label="Category" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                                <MenuItem value="">All</MenuItem>
                                {['OPEN', 'OBC', 'SC', 'ST', 'NT', 'VJNT', 'SBC'].map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField fullWidth select size="small" label="Class" value={classFilter} onChange={(e) => setClassFilter(e.target.value)}>
                                <MenuItem value="">All</MenuItem>
                                {uniqueClasses.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                            </TextField>
                        </Grid>
                    </Grid>

                    {loading ? <Box sx={{ py: 5, textAlign: 'center' }}><CircularProgress /></Box> : (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                        <TableCell sx={{ fontWeight: 700 }}>Student</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Scheme</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>Disbursed</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 700 }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredData.length === 0 ? (
                                        <TableRow><TableCell colSpan={6} align="center">No scholarship records found</TableCell></TableRow>
                                    ) : filteredData.map(s => {
                                        const student = s.Student || {};
                                        return (
                                            <TableRow key={s.id} hover>
                                                <TableCell sx={{ fontWeight: 600 }}>
                                                    {student.user?.firstName} {student.user?.lastName}
                                                    <Typography variant="caption" display="block" color="text.secondary">
                                                        {student.Class?.name} | {student.category}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>{s.schemeName}</TableCell>
                                                <TableCell align="right">₹{Number(s.amount).toLocaleString()}</TableCell>
                                                <TableCell align="right" sx={{ color: '#4caf50', fontWeight: 600 }}>₹{Number(s.disbursedAmount).toLocaleString()}</TableCell>
                                                <TableCell>
                                                    <Chip size="small" label={s.status}
                                                        color={s.status === 'Disbursed' ? 'success' : s.status === 'Partial' ? 'warning' : s.status === 'Applied' ? 'info' : 'default'} />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Tooltip title="Edit"><IconButton size="small" color="primary" onClick={() => handleOpenAssign(s)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                                                    <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDelete(s.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Box>
            </Card>

            {/* Assign/Edit Dialog */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{selectedId ? 'Edit Scholarship' : 'Assign Scholarship'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <Autocomplete
                            options={students}
                            getOptionLabel={(opt) => `${opt.user?.firstName} ${opt.user?.lastName} (${opt.rollNumber})`}
                            value={students.find(s => s.id === formData.studentId) || null}
                            onChange={(e, v) => setFormData({ ...formData, studentId: v ? v.id : '' })}
                            renderInput={(params) => <TextField {...params} label="Select Student" required />}
                            disabled={!!selectedId}
                        />
                        <TextField label="Scholarship Scheme Name" fullWidth value={formData.schemeName} onChange={(e) => setFormData({ ...formData, schemeName: e.target.value })} required />
                        
                        <Grid container spacing={2}>
                            <Grid item xs={6}><TextField label="Total Amount (₹)" fullWidth type="number" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} required /></Grid>
                            <Grid item xs={6}><TextField label="Disbursed (₹)" fullWidth type="number" value={formData.disbursedAmount} onChange={(e) => setFormData({ ...formData, disbursedAmount: e.target.value })} /></Grid>
                        </Grid>

                        <TextField select label="Status" fullWidth value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })}>
                            {['Applied', 'Pending', 'Partial', 'Disbursed', 'Rejected'].map(st => <MenuItem key={st} value={st}>{st}</MenuItem>)}
                        </TextField>

                        <Grid container spacing={2}>
                            <Grid item xs={6}><TextField label="Bank Name" fullWidth value={formData.bankName} onChange={(e) => setFormData({ ...formData, bankName: e.target.value })} /></Grid>
                            <Grid item xs={6}><TextField label="Account No" fullWidth value={formData.accountNo} onChange={(e) => setFormData({ ...formData, accountNo: e.target.value })} /></Grid>
                        </Grid>

                        <TextField label="Disbursement Date" fullWidth type="date" InputLabelProps={{ shrink: true }} value={formData.disbursementDate} onChange={(e) => setFormData({ ...formData, disbursementDate: e.target.value })} />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                    <Button variant="contained" disabled={submitting} onClick={handleSubmit}>
                        {submitting ? <CircularProgress size={24} /> : (selectedId ? 'Update' : 'Assign')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Scholarship;
